import React, { useState, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Spin,
  Alert,
  Space,
  Button,
  Tag,
  Modal,
  DatePicker
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  BarChartOutlined,
  CalendarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { getUserActivities, getTaskStatistics } from '../../utils/taskStatistics';
const { Title, Text, Paragraph } = Typography;

interface TypeStat {
  data_type: string;
  count: number;
  task_type: string;
  handler_name: string;
  service_name: string;
  service_names?: string[];
}

interface RealActivityStats {
  daily_activity: Array<{ date: string; submissions: number }>;
  task_stats: Array<{ task_type: string; count: number }>;
  active_users: number;
  total_submissions: number;
}

const DataAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [typeStats, setTypeStats] = useState<TypeStat[]>([]);
  const [rawServiceStats, setRawServiceStats] = useState<Array<{ service_name: string; task_type: string; total: number; successful: number; failed: number; avg_time: number }>>([]);
  const [realActivity, setRealActivity] = useState<RealActivityStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  // 时间范围（默认近6个月）
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'month'), dayjs()]);
  const [daysWindow, setDaysWindow] = useState<number>(180);

  // 活动趋势弹窗相关状态
  const [trendOpen, setTrendOpen] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendTaskType, setTrendTaskType] = useState<string | null>(null);
  const [trendDailyStats, setTrendDailyStats] = useState<Array<{ date: string; total: number; successful: number; failed: number }>>([]);


  const fetchAllData = async (days?: number) => {
    setLoading(true);
    setError(null);
    try {
      // 获取任务统计
      const periodDays = days ?? daysWindow;
      const taskStats = await getTaskStatistics(periodDays);
      setRawServiceStats(taskStats.service_stats || []);
      // 聚合：按 handler_name 汇总（实现细粒度统计）
      const aggregatedByHandler = taskStats.service_stats.reduce((acc, item) => {
        const key = item.handler_name || item.task_type;
        if (!acc[key]) {
          acc[key] = { 
            data_type: item.handler_name || item.task_type, 
            count: 0, 
            task_type: item.task_type, 
            handler_name: item.handler_name || '',
            service_name: item.service_name || '', 
            service_names: [] as string[] 
          };
        }
        acc[key].count += item.total || 0;
        if (item.service_name && !acc[key].service_names!.includes(item.service_name)) {
          acc[key].service_names!.push(item.service_name);
        }
        return acc;
      }, {} as Record<string, TypeStat>);
      setTypeStats(Object.values(aggregatedByHandler));

      // 获取用户活跃/活动
      const userActivities = await getUserActivities(periodDays);
      setRealActivity({
        daily_activity: userActivities.daily_activities.map(item => ({
          date: item.date,
          submissions: item.total
        })),
        task_stats: taskStats.service_stats.map(item => ({
          task_type: item.handler_name || item.task_type,
          count: item.total
        })),
        active_users: userActivities.daily_activities.reduce((max, cur) =>
          Math.max(max, cur.unique_visitors), 0),
        total_submissions: userActivities.daily_activities.reduce((sum, cur) =>
          sum + cur.total, 0)
      });

    } catch (err) {
      setError('获取数据失败');
      console.error('获取数据失败:', err);
    }
    setLastRefresh(new Date());
    setLoading(false);
  };

  // useEffect和handleRefresh统一调用fetchAllData
  useEffect(() => { fetchAllData(daysWindow); }, [daysWindow]);
  const handleRefresh = () => fetchAllData(daysWindow);

  // 时间范围变化
  const onRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const validRange: [Dayjs, Dayjs] = [dates[0], dates[1]];
    setRange(validRange);
    const ms = Math.abs(validRange[1].startOf('day').toDate().getTime() - validRange[0].startOf('day').toDate().getTime());
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    setDaysWindow(days);
    fetchAllData(days);
  };

  // 打开某任务类型的活动趋势弹窗
  const openTrendModal = async (record: TypeStat) => {
    const displayName = record.data_type;
    console.log('=== 查看趋势弹窗调试信息 ===');
    console.log('选中记录:', record);
    console.log('service_name:', record.service_name);
    console.log('task_type:', record.task_type);
    console.log('daysWindow:', daysWindow);
    
    setTrendTaskType(displayName);
    setTrendOpen(true);
    setTrendLoading(true);
    try {
      // 使用具体的 service_name 和 task_type 进行查询
      // 根据原始 service_stats 解析该记录对应的真实维度
      // 规则：优先匹配 task_type == 记录的 task_type；若没有，则匹配 service_name == 记录的 task_type（有些项目可能把服务名放在task_type里展示）
      const pairs: Array<{ service_name?: string; task_type?: string }> = [];
      const matchedByTaskType = rawServiceStats.filter(s => s.task_type === record.task_type);
      if (matchedByTaskType.length > 0) {
        for (const s of matchedByTaskType) pairs.push({ service_name: s.service_name, task_type: s.task_type });
      } else {
        const matchedByService = rawServiceStats.filter(s => s.service_name === record.task_type);
        for (const s of matchedByService) pairs.push({ service_name: s.service_name, task_type: s.task_type });
      }

      if (pairs.length === 0) {
        // 兜底：仅按 task_type 过滤一次
        const stats = await getTaskStatistics(daysWindow, undefined, record.task_type);
        setTrendDailyStats(stats.daily_stats || []);
      } else {
        // 逐对查询并合并
        const allStats = await Promise.all(
          pairs.map(p => getTaskStatistics(daysWindow, p.service_name, p.task_type))
        );
        const merged: Record<string, { date: string; total: number; successful: number; failed: number }> = {};
        for (const res of allStats) {
          for (const d of (res.daily_stats || [])) {
            const key = d.date;
            if (!merged[key]) merged[key] = { date: d.date, total: 0, successful: 0, failed: 0 };
            merged[key].total += d.total || 0;
            merged[key].successful += d.successful || 0;
            merged[key].failed += d.failed || 0;
          }
        }
        const mergedList = Object.values(merged).sort((a, b) => a.date.localeCompare(b.date));
        setTrendDailyStats(mergedList);
      }
    } catch (e) {
      console.error('获取任务类型趋势失败: ', displayName, e);
      setTrendDailyStats([]);
    } finally {
      setTrendLoading(false);
    }
  };

  const getTrendOption = () => ({
    title: { text: trendTaskType ? `${trendTaskType} 活动趋势（${daysWindow}天）` : '活动趋势', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: { data: ['总数', '成功', '失败'], top: 24 },
    xAxis: { type: 'category', data: trendDailyStats.map(d => d.date) },
    yAxis: { type: 'value' },
    series: [
      { name: '总数', type: 'line', smooth: true, data: trendDailyStats.map(d => d.total), itemStyle: { color: '#1890ff' } },
      { name: '成功', type: 'line', smooth: true, data: trendDailyStats.map(d => d.successful), itemStyle: { color: '#52c41a' } },
      { name: '失败', type: 'line', smooth: true, data: trendDailyStats.map(d => d.failed), itemStyle: { color: '#f5222d' } },
    ]
  });

  // 已移除数据提交记录表格

  // 任务类型统计图表配置
  const getTaskStatsChartOption = () => ({
    title: {
      text: '任务类型统计',
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    series: [{
      name: '任务类型',
      type: 'pie',
      radius: '50%',
      data: typeStats.map(item => ({
        name: item.data_type,
        value: item.count
      }))
    }]
  });

  // 每日活动图表配置
  const getDailyActivityChartOption = () => ({
    title: {
      text: '每日数据提交活动',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: realActivity?.daily_activity.map(item => item.date) || []
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      data: realActivity?.daily_activity.map(item => item.submissions) || [],
      type: 'bar',
      itemStyle: {
        color: '#1890ff'
      }
    }]
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载数据分析中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="数据加载失败"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        action={
          <Button size="small" danger onClick={handleRefresh}>
            重试
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>数据分析</Title>
        <Space>
          <Text type="secondary">
            最后更新: {lastRefresh.toLocaleTimeString()}
          </Text>
          <DatePicker.RangePicker
            allowClear={false}
            value={range}
            onChange={onRangeChange}
            presets={[
              { label: '近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
              { label: '近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
              { label: '近3个月', value: [dayjs().subtract(3, 'month'), dayjs()] },
              { label: '近6个月', value: [dayjs().subtract(6, 'month'), dayjs()] },
              { label: '近1年', value: [dayjs().subtract(12, 'month'), dayjs()] },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            刷新数据
          </Button>
        </Space>
      </div>

      {/* 概览统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总数据提交"
              value={realActivity?.total_submissions || 0} // 使用实际的总提交数
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活跃用户"
              value={realActivity?.active_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="任务类型数"
              value={typeStats.length}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="本周提交"
              value={realActivity?.total_submissions || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="任务类型分布" style={{ height: 400 }}>
            <ReactECharts
              option={getTaskStatsChartOption()}
              style={{ height: 300 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="每日活动趋势" style={{ height: 400 }}>
            <ReactECharts
              option={getDailyActivityChartOption()}
              style={{ height: 300 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 任务类型统计表格 */}
      <Card title="任务类型统计" style={{ marginBottom: 24 }}>
        <Table
          rowKey={(r) => r.data_type}
          dataSource={typeStats}
          columns={[
            {
              title: '任务类型',
              dataIndex: 'data_type',
              key: 'data_type',
              render: (text: string) => <Tag color="blue">{text}</Tag>
            },
            {
              title: '提交次数',
              dataIndex: 'count',
              key: 'count',
              sorter: (a: TypeStat, b: TypeStat) => a.count - b.count,
              render: (count: number) => (
                <Text strong style={{ color: '#1890ff' }}>
                  {count.toLocaleString()}
                </Text>
              )
            },
            {
              title: '活动趋势',
              key: 'trend',
              render: (_: any, record: TypeStat) => (
                <Button type="link" icon={<BarChartOutlined />} onClick={() => openTrendModal(record)}>
                  查看趋势
                </Button>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      </Card>

      {/* 活动趋势弹窗 */}
      <Modal
        open={trendOpen}
        title={trendTaskType ? `${trendTaskType} 活动趋势` : '活动趋势'}
        onCancel={() => setTrendOpen(false)}
        footer={null}
        width={800}
      >
        <div style={{ height: 420 }}>
          {trendLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : (
            <ReactECharts option={getTrendOption()} style={{ height: 360 }} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DataAnalysis;